import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { ThemeContext } from 'styled-components';
// Import Shared Components
import {
  Loader,
  ErrorComponent,
  ButtonGroup,
  IconButton,
} from 'shared-components';
// Import Shared Utils
import { get, deleteHTTP } from 'shared-components/src/utils/http';
// Import Global Components
import Heading from '../../../../../../components/Heading';
import Alert from '../../../../../../components/Alert';
import Button from '../../../../../../components/Button';
// Import Local Components
import AccountDetailsCard from './components/AccountDetailsCard';
import AccountTransfersCard from './components/AccountTransfersCard';
import EditExternalAccountModal from '../../../../components/EditExternalAccountModal';
import ConfirmDeleteModal from '../../../../components/ConfirmDeleteModal';
// Import Component Styles
import { HeadingWrapper } from '../../../../styles';
import { ButtonsContainer } from '../../styles';

const ExternalAccountDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const theme = useContext(ThemeContext);

  const page = 'external-account-details';

  // Component states
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accountDetails, setAccountDetails] = useState(null);
  const [alert, setAlert] = useState(null);

  // Edit/Delete modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch account details
  useEffect(() => {
    const fetchAccountDetails = async () => {
      try {
        const response = await get(
          `/api/v2/cardholder_portal/cardholder/external_accounts/${id}/details`
        );
        setAccountDetails(response);
      } catch (e) {
        setError({
          type: 'danger',
          message: e.message || 'Failed to load account details',
        });
      }
      setLoading(false);
    };

    fetchAccountDetails();
  }, [id]);

  // Handle back navigation
  const handleBack = e => {
    e.preventDefault();
    navigate('/transfers/external-accounts');
  };

  // Handle edit account
  const handleEdit = e => {
    e.preventDefault();
    setShowEditModal(true);
  };

  // Handle account updated
  const handleAccountUpdated = updatedAccount => {
    setAccountDetails(updatedAccount);
  };

  // Handle delete account
  const handleDeleteAccount = e => {
    e.preventDefault();
    setShowDeleteModal(true);
  };

  // Close delete modal
  const closeDeleteModal = e => {
    e.preventDefault();
    setShowDeleteModal(false);
  };

  // Handle delete
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteHTTP(
        `/api/v2/cardholder_portal/cardholder/external_accounts/${id}`
      );
      navigate('/transfers/external-accounts');
    } catch (e) {
      setAlert({
        type: 'danger',
        message:
          e.message ||
          intl.messages[`${page}-delete-error-alert`] ||
          'Failed to delete external account',
        messageFor: 'delete-error',
      });
      setShowDeleteModal(false);
    }
    setDeleteLoading(false);
  };

  // Define page states
  if (error) return <ErrorComponent />;
  if (loading) return <Loader />;
  if (!accountDetails) {
    return (
      <Alert
        type="danger"
        alert={intl.messages[`${page}-not-found-alert`] || 'Account not found'}
        alertFor="not-found"
        page={page}
      />
    );
  }

  return (
    <>
      <HeadingWrapper>
        <Heading
          page={page}
          heading={intl.messages[`${page}-heading`] || 'Account Details'}
        />
        <ButtonsContainer>
          <ButtonGroup justifyContent="flex-end">
            <IconButton
              iconType="edit"
              onClick={handleEdit}
              color={theme.colors.lightBlue}
              title={
                intl.messages[`${page}-edit-button`] || 'Edit External Account'
              }
            />
            <IconButton
              iconType="trash"
              onClick={handleDeleteAccount}
              color={theme.colors.danger}
              title={
                intl.messages[`${page}-delete-button`] ||
                'Delete External Account'
              }
            />
          </ButtonGroup>
          <Button
            buttonText={
              intl.messages[`${page}-back-button`] || 'Back to Accounts'
            }
            buttonFor="back"
            page={page}
            onClick={handleBack}
            type="default"
          />
        </ButtonsContainer>
      </HeadingWrapper>

      {alert && (
        <Alert
          type={alert.type}
          alert={alert.message}
          alertFor={alert.messageFor}
          page={page}
        />
      )}

      {/* Account Details Card */}
      <AccountDetailsCard accountDetails={accountDetails} page={page} />

      {/* Transfers Card */}
      <AccountTransfersCard accountId={id} page={page} />

      {/* Edit External Account Modal */}
      {showEditModal && (
        <EditExternalAccountModal
          toggleModal={setShowEditModal}
          setSuccess={setAlert}
          onAccountUpdated={handleAccountUpdated}
          id={id}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && accountDetails && (
        <ConfirmDeleteModal
          toggleModal={closeDeleteModal}
          loading={deleteLoading}
          alert={null}
          deleteItemValue={String(id)}
          deleteItemName={accountDetails.name}
          deleteCallback={handleDelete}
          page={page}
        />
      )}
    </>
  );
};

export default ExternalAccountDetails;
